<script lang="ts">
	import ProductDeliveryTimes from '$lib/components/product/ProductDeliveryTimes.svelte'
	import ProductRandomNotice from './ProductRandomNotice.svelte'
	import ProductBuy from '$lib/components/product/ProductBuy.svelte'
	import { addToCart } from '$lib/js/cart'
	import { PUBLIC_BRANDS_MEDIA } from '$env/static/public'
	import { page } from '$app/stores'
	import {
		getProductFinalPriceWithTax,
		getProductRegularPriceWithTax,
		getProductRetailPriceWithTax,
		getProductSpecialPriceWithTax,
		getProductTierPrices,
		getProductTierPriceWithTax,
		getPromotionEndDate,
		getPercentageSaved,
		is_set_array,
		is_set_object_type,
	} from '$lib/js/helpers'
	import { beforeNavigate } from '$app/navigation'
	import { t } from 'svelte-i18n-lingui'
	import type { ConfigurableChild, FullProduct } from '$lib/js/elasticsearch/types'
	import { checkStock } from '$lib/js/products'
	import '$lib/css/components/select.scss'
	import ProductConfigurations from './ProductConfigurations.svelte'
	import ProductStars from './ProductStars.svelte'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { getFormattedPrice } from '$lib/js/helpers/prices'
	import { storeConfigurableChild } from '$lib/js/stores'

	export let product: FullProduct
	export let qtyselected = 1
	const scope = getContextScope()

	let brand = $page.data.brand

	$: uClass = checkStock(product)

	$: retail_price = getProductRetailPriceWithTax(product)
	$: special_price = getProductSpecialPriceWithTax(product)
	$: regular_price = getProductRegularPriceWithTax(product)
	$: percentage_saved = getPercentageSaved(retail_price, regular_price, special_price)

	beforeNavigate(() => {
		uClass = checkStock(product)
	})

	/* Special tier prices */
	let tierPrices = getProductTierPrices(product)

	$: if (getProductTierPriceWithTax(product, qtyselected)) {
		const tier_price = getProductTierPriceWithTax(product, qtyselected)
		if (special_price && special_price < regular_price) {
			special_price = tier_price
		} else {
			regular_price = tier_price
		}
	} else if (product.type_id === 'configurable' && is_set_object_type<ConfigurableChild>($storeConfigurableChild)) {
    special_price = getProductSpecialPriceWithTax($storeConfigurableChild)
		regular_price = getProductRegularPriceWithTax($storeConfigurableChild)
		retail_price = getProductRetailPriceWithTax($storeConfigurableChild)
  }

	async function addItemToCart(product: FullProduct) {
		await addToCart(product, 1)
	}

	// PROMO: promo date
	$: promo_date = getPromotionEndDate(product)
</script>


<div class="product-price">
	<div class="brand">
		{#if brand && brand.image}
			<a href="/{scope}/brands/{brand.slug}" class="brand__img">
				<img
					src="{PUBLIC_BRANDS_MEDIA}{brand.image}"
					alt="{brand.page_title}"
					title="{brand.page_title}"
					width=83
					height=34
				>
			</a>
		{:else if brand && brand.page_title}
			<a href="/{scope}/brands/{brand.slug}" class="brand__img text">
				<p>{brand.page_title}</p>
			</a>
		{/if}

		<div class="sku-info">
			{#if product.stock.source_quantity > 0}
				<p class="availability">{$t`Available`}</p>
			{:else}
				<p class="availability no-stock">{$t`No stock`}</p>
			{/if}
			<p class="sku">{"SKU:"} {product.sku}</p>
		</div>
	</div>

	<ProductStars {product} />

	<div class="shipping-info">
		{#if getProductFinalPriceWithTax(product) < 69}
			<p class="shipping">{$t`Free shipping from ${69}€`}</p>
		{:else}
			<p class="shipping">{$t`Free shipping`}</p>
		{/if}

		<!-- <p class="order-preparation"><img src="/images/icons/rocket.svg" alt="Enviamos en 24h" title="Enviamos en 24h"> {"Enviamos en 24h"}</p> -->
	</div>
	<ProductDeliveryTimes {product} />


	{#if is_set_array(tierPrices)}
		<div class="packs">
			{#each tierPrices as pack}
				<p class="pack">{$t`Buy ${pack.qty} for ${getFormattedPrice(pack.value_with_tax)}€ each one and save ${(100 - (pack.value_with_tax * 100 / getProductFinalPriceWithTax(product))).toFixed(2)}%!`}</p>
			{/each}
		</div>
	{/if}

	<ProductConfigurations {product} />

	<div class="price-wrapper">
		<!-- BX. Special product promo. -->
		{#if special_price && special_price < regular_price
    || regular_price < retail_price}
			<div class="special-banner">
				<div class="special-banner__inner">Promo primavera</div>
			</div>
		{/if}
		<!-- BX. Special product promo. -->

		<ProductRandomNotice {product} />

		<div class="price {uClass}" data-role="priceBox">
			{#if special_price && special_price < regular_price}
				<p class="recommended">{$t`Common price:`}
					<span>{getFormattedPrice(regular_price)}€</span>
				</p>

				<h2 class="special-price">
					<span class="total">{getFormattedPrice(special_price)}€
					</span>
					<span class="savings">{$t`Save ${getFormattedPrice(regular_price - special_price)} (${percentage_saved}%)`}</span>
				</h2>
			{:else if regular_price < retail_price}
				<p class="recommended">{$t`Recommended price:`}
					<span>{getFormattedPrice(retail_price)}€</span>
				</p>

				<h2 class="special-price">
					<span class="total">{getFormattedPrice(regular_price)}€
					</span>
					<span class="savings">{$t`Save ${getFormattedPrice(retail_price - regular_price)} (${percentage_saved}%)`}</span>
				</h2>
			{:else}
				<h2 class="regular-price">
					<span class="total">{getFormattedPrice(regular_price)}€</span>
				</h2>
			{/if}

			<!-- PROMO: valid until -->
			{#if promo_date && special_price}
				<p class="promo">{$t`*Valid promotion until ${promo_date}`}</p>
			{/if}
		
			{#if product.stock.source_quantity === 1 && product.type_id !== "configurable"}
				<p class="low-stock">{$t`Only 1 article in stock`}</p>
			{/if}
		</div>
	
		{#if product.stock.source_quantity > 0}
			<button class="u-btn buy-btn" on:click="{()=> addItemToCart(product)}">
				<span class="icon icon-add-to-cart"></span>{$t`Add to cart`}
			</button>
		{/if}

		<ProductBuy {product} bind:qtyselected/>
	</div>
</div>


<style lang="scss">
  // BX start. Special banner. Product price label
  .special-banner {
    position: absolute;
    top: -12px;
    right: -8px;
    display: flex;
    justify-content: flex-end;

    &__inner {
      padding: 4px 12px;
      background: var(--main);
      display: inline-block;
      color: white;
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 700;
      margin-bottom: 4px;
      transform: rotate(4deg);
    }
  }
  // BX end. Special banner. Product price label

  .price-wrapper {
    position: relative;
		background: white;
		border-radius: 10px;
  }

  .packs {
    padding: 8px 12px 32px;
    background: #34c9a2;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin-bottom: -25px;
  }

  .order-preparation {
    display: flex;
    align-items: center;
    color: var(--main);
    margin-top: 8px;

    img {
      width: 20px;
      margin-right: 6px;
    }
  }

  .low-stock {
		margin-top: 10px;
    color: var(--red);
  }

  .buy-btn {
    padding: 12px 25px;
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;

    @media (max-width: 768px) {
			position: relative;
    	box-shadow: 0px -6px 8px 2px rgba(0, 0, 0, 0.08);
      width: 100%;
    }

    @media (min-width: 769px) {
      display: none;
      visibility: hidden;
      opacity: 0;
    }

    span {
      margin-right: 10px;
    }
  }

  .brand {
    display: flex;
    align-items: center;
    padding-bottom: 10px;

    &__img {
      width: 80px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
      background: white;
      margin-right: 15px;
      filter: drop-shadow(0 0 24px rgba(0, 0, 0, 0.12));

      &.text {
        width: 120px;
        color: black;

        &:hover {
          color: var(--main);
        }

        p {
          text-align: center;
        }
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .availability {
      text-transform: uppercase;
      font-weight: 700;
      color: var(--secondary);
      margin-bottom: 3px;

      @media (min-width: 769px) {
        font-size: 16px;
      }

      &.no-stock {
        color: black;
      }
    }

    .sku-info {
      min-width: 110px;
    }
  }

  .shipping {
    font-size: 16px;
    color: var(--main);
    font-weight: 700;
    margin: 5px 15px 0 0;

    &-info {
      flex-wrap: wrap;
      display: flex;
      align-items: center;
      margin-top: 8px;
    }
  }

  .price {
    display: inline-block;
    background: white;
    padding: 20px 20px 18px;
    border-radius: 8px;
    width: calc(100% - 40px);

    &.no-stock {
      .special-price {
        color: var(--grey--b);

        .savings {
          border-color: var(--grey--b);
        }
      }
    }
  }

  .promo {
    font-size: 14px;
		text-transform: uppercase;
		font-weight: 900;
		color: var(--main);
		margin-top: 8px;
  }

  .recommended {
    margin-bottom: 5px;

    span {
      text-decoration: line-through;
    }
  }

  .special-price,
  .regular-price {
    font-size: 36px;
    color: var(--main);
    margin-top: 3px;
    margin-bottom: 3px;
    display: flex;
    align-items: center;

    span.total {
      line-height: 1;
      margin-right: 10px;
			font-weight: 900;
    }

    span.savings {
      font-size: 14px;
      padding: 6px 10px;
      display: inline-block;
      border: 1px solid var(--main);
    }
  }
</style>
